import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../services/api.tsx";
import { useAuth } from "../contexts/AuthContext.tsx";
import { useError } from "../contexts/ErrorContext.tsx";

interface CartItem {
  id: string;
  productId: string;
  name: string;
  price: number;
  finalPrice: number;
  quantity: number;
  stock: number;
  campaignId?: string;
  depositRate?: number;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
  total: number;
  fetchCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const { isAuthenticated } = useAuth();
  const { setError } = useError();

  // Fetch cart from backend on mount
  useEffect(() => {
    const fetchCart = async () => {
      try {
        if (isAuthenticated) {
          const token = localStorage.getItem("access_token");
          const res = await api.get("/api/cart", {
            headers: { Authorization: `Bearer ${token}` },
          });
          setCart(res.data);
        } else {
          setCart([]); // if not authenticated, clear cart
        }
      } catch (err) {
        setCart([]); // fallback to empty cart
      }
    };
    fetchCart();
  }, [isAuthenticated]);

  // Helper to sync cart to backend
  const syncCart = async (item: CartItem, action: string) => {
    try {
      if (isAuthenticated) {
        const token = localStorage.getItem("access_token");
        switch (action) {
          case "add":
            return await api.post("/api/cart", item, {
              headers: { Authorization: `Bearer ${token}` },
            });
          case "remove":
            return await api.delete(`/api/cart/${item.productId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
          default:
            return null;
        }
      }
      // Eğer ürün kampanyalıysa ve giriş yapılmamışsa hata göster
      if (item.campaignId) {
        setError("Kampanyalı ürünleri sepete eklemek için lütfen giriş yapın");
      }
      // Kampanyasız ürünler için hata gösterme, local cart'a ekle
      return null;
    } catch (err) {
      setError(err.response?.data?.Message || "Bir hata oluştu.");
      return undefined;
    }
  };

  const addToCart = async (item: CartItem) => {
    let isSuccess = true;
    setCart((prev) => {
      const existing = prev.find((p) => p.productId === item.productId);
      let updatedCart;
      if (existing) {
        const newQuantity = existing.quantity + item.quantity;
        if (newQuantity > item.stock) {
          isSuccess = false;
          setError("Stock Adedini Aştınız!");
          updatedCart = prev.map((p) => p);
        } else {
          updatedCart = prev.map((p) =>
            p.productId === item.productId ? { ...p, quantity: newQuantity } : p
          );
        }
      } else {
        if (item.quantity > item.stock) {
          isSuccess = false;
          setError("Stock Adedini Aştınız!");
          updatedCart = prev.map((p) => p);
        } else {
          updatedCart = [...prev, item];
        }
      }
      return updatedCart;
    });
    // Sadece giriş yapılmışsa veya ürün kampanyalıysa backend'e gönder
    if (isAuthenticated || item.campaignId) {
      let response = await syncCart(item, "add");
      await fetchCart();
      return response;
    }
    return { success: isSuccess };
  };

  const removeFromCart = (id: string) => {
    setCart((prev) => {
      const itemToRemove = prev.find((item) => item.productId === id);
      const updatedCart = prev.filter((item) => item.productId !== id);
      if (itemToRemove) {
        syncCart(itemToRemove, "remove");
      }
      return updatedCart;
    });
  };

  // Move fetchCart out so it can be called after syncCart
  const fetchCart = async () => {
    try {
      if (isAuthenticated) {
        const token = localStorage.getItem("access_token");
        const res = await api.get("/api/cart", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCart(Array.isArray(res.data) ? res.data : []);
      } else {
        setCart([]); // if not authenticated, clear cart
      }
    } catch (err) {
      setCart([]); // fallback to empty cart
    }
  };

  const clearCart = () => {
    setCart([]);
  };

  const total = Array.isArray(cart) ? cart.reduce((sum, item) => sum + item.finalPrice * item.quantity, 0) : 0;

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, clearCart, total, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used inside a CartProvider");
  return context;
};